import { useState, type FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import cn from 'classnames';

import { errorToast, successToast } from '@/helpers';

import InVivoError from '@/model/InVivoError';
import Http from '@/support/http';
import { api as apiRoute } from '@/support/route';
import { ExceptionHandler } from '@/utils/ExceptionHandler';

import ApiErrorBanner from '@/components/ApiErrorBanner';
import Banner from '@/components/UI/Banner';
import Button from '@/components/UI/Button';
import Text from '@/components/UI/FormFields/Latest/Text';

interface Treatment {
  id: number;
  name: string;
}

interface EditTreatmentFormData {
  name: string;
}

interface EditTreatmentProps {
  treatment: Treatment;
  onEditTreatment: (data: Treatment) => void;
}

const EditTreatment: FC<EditTreatmentProps> = ({ treatment, onEditTreatment }) => {
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<Error | false>(false);
  const formMethods = useForm<EditTreatmentFormData>({
    defaultValues: {
      name: treatment.name,
    },
  });
  const { handleSubmit } = formMethods;
  const dispatch = useDispatch();

  const closeModal = () => dispatch({ type: 'CLOSE_MODAL' });

  const onSubmit = async (data: EditTreatmentFormData) => {
    try {
      setApiError(false);
      setLoading(true);
      await Http.patch(apiRoute('team.glossary.treatment.update', { id: treatment.id }), data);
      onEditTreatment({ ...treatment, ...data });
      closeModal();
      successToast('Treatment updated successfully');
    } catch (error) {
      const wrappedError = error instanceof Error ? error : new Error(String(error));
      setApiError(wrappedError);
      errorToast('Failed to update treatment. Please try again.');
      ExceptionHandler.captureException(
        new InVivoError('Failed to update treatment', { cause: error, slug: 'edit-treatment-submit' })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)} className={cn('ui-card center mw6', { ui__disabled: loading })}>
        <div className="pa3">
          <h3 className="f4 lh-title normal">Edit treatment</h3>
        </div>
        <div className="pa3 pt0">
          <Banner info dismiss={false}>
            Editing this treatment will update all past and future dosages that reference it.
          </Banner>
        </div>
        {apiError instanceof Error && <ApiErrorBanner />}
        <div className="pa3">
          <Text label="Name" name="name" required />
        </div>
        <div className="pa3 mt3 bt b--moon-gray">
          <Button submit loading={loading}>
            Save
          </Button>
          <Button plain className="ml3" onClick={closeModal}>
            Cancel
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default EditTreatment;
